/**
 * Typed wrappers around the better-artifacts REST API.
 *
 * All requests go through fetch with `credentials: "include"` so the
 * session cookie is sent on every call (Vite proxies /api/* to the backend
 * on its assigned port in development).
 */

export interface UserAuth {
  id: string;
  email: string;
  display_name: string;
  role: string;
  is_active: boolean;
}

export interface AdminUserView {
  id: string;
  email: string;
  display_name: string;
  role: "user" | "admin";
  is_active: boolean;
  last_seen_at: string | null;
}

export interface AdminArtifactView {
  slug: string;
  title: string;
  description: string;
  visibility: Visibility;
  owner_email: string;
  url: string;
  created_at: string;
  updated_at: string;
  expires_at: string;
  size_bytes: number;
  view_count: number;
  last_viewed_at: string | null;
  permanent: boolean;
  deleted_at: string | null;
}

export interface AdminUsageRow {
  email: string;
  display_name: string;
  role: "user" | "admin";
  is_active: boolean;
  artifact_count: number;
  total_bytes: number;
  requests_7d: number;
  tokens_7d: number;
  requests_30d: number;
  tokens_30d: number;
  last_token_use: string | null;
}

export interface AdminEffectiveQuotas {
  max_user_storage_bytes: number;
  anthropic_rate_limit_per_hour: number;
  anthropic_token_limit_per_day: number;
}

export interface AdminQuotasView {
  email: string;
  effective: AdminEffectiveQuotas;
  overrides: Partial<AdminEffectiveQuotas>;
}

export interface AdminQuotasPatch {
  max_user_storage_bytes?: number | null;
  anthropic_rate_limit_per_hour?: number | null;
  anthropic_token_limit_per_day?: number | null;
  clear?: (keyof AdminEffectiveQuotas)[];
}

/** Sort keys for the admin "All Artifacts" governance table (clickable headers). */
export type AdminSortKey =
  | "title"
  | "owner"
  | "visibility"
  | "size"
  | "views"
  | "last_viewed"
  | "ttl"
  | "updated";

export interface AdminArtifactFilters {
  visibility?: Visibility;
  owner?: string;
  status?: "active" | "deleted" | "all";
  q?: string;
  /** Filter to artifacts whose last_viewed_at is null OR older than now-staleDays. */
  stale_days?: number;
  /** Sort key; defaults server-side to ``updated``. */
  sort?: AdminSortKey;
  /** Sort direction; defaults server-side to ``desc``. */
  direction?: "asc" | "desc";
  limit?: number;
  offset?: number;
}

export interface AuditLogEntry {
  id: string;
  actor_email: string;
  actor_kind: string;
  event_type: string;
  target_slug: string | null;
  ip: string;
  user_agent: string;
  outcome: string;
  metadata: Record<string, unknown>;
  created_at: string | null;
}

export interface AuditLogFilters {
  actor?: string;
  event_type?: string;
  outcome?: string;
  since?: string;
  until?: string;
  limit?: number;
  offset?: number;
}

export interface AdminTokenView {
  id: string;
  owner_email: string;
  kind: "static" | "oauth";
  label: string;
  prefix_suffix: string;
  created_at: string;
  last_used_at: string | null;
  enabled: boolean;
}

export interface AdminTokenCreated extends AdminTokenView {
  raw: string;
}

export type CdnDirective =
  "script_src" | "style_src" | "font_src" | "img_src" | "connect_src";

export interface CdnAllowlistDoc {
  script_src: string[];
  style_src: string[];
  font_src: string[];
  img_src: string[];
  connect_src: string[];
  updated_at: string;
  updated_by: string;
}

export interface CdnAllowlistInput {
  script_src: string[];
  style_src: string[];
  font_src: string[];
  img_src: string[];
  connect_src: string[];
}

export interface SharingSettingsDoc {
  allowed_share_domains: string[];
  updated_at: string | null;
  updated_by: string;
}

export interface LifecycleConfigDoc {
  artifact_ttl_days: number;
  soft_delete_grace_days: number;
  bump_on_view_window_days: number;
  updated_at: string;
  updated_by: string;
}

export interface LifecycleConfigInput {
  artifact_ttl_days: number;
  soft_delete_grace_days: number;
  bump_on_view_window_days: number;
}

/** PUT response: the new config plus how many existing artifacts had their
 *  expiry extended (non-zero only when the retention period was increased). */
export interface LifecyclePutResponse extends LifecycleConfigDoc {
  expiry_recomputed: number;
}

/** Slack alert settings as returned by the admin API (webhook URL masked). */
export interface AdminAlertSettings {
  enabled: boolean;
  webhook_configured: boolean;
  /** Last few chars of the configured webhook (e.g. "…1234"), never the URL. */
  webhook_hint: string;
  channel_label: string;
  categories: Record<string, boolean>;
  dedup_cooldown_seconds: number;
  updated_at: string | null;
  updated_by: string;
}

/** Partial update body. A blank/omitted slack_webhook_url keeps the existing one. */
export interface AdminAlertSettingsPatch {
  enabled?: boolean;
  slack_webhook_url?: string;
  channel_label?: string;
  categories?: Record<string, boolean>;
  dedup_cooldown_seconds?: number;
}

/** Result of POST /api/admin/alerts/test. */
export interface AlertTestResult {
  ok: boolean;
  error: string | null;
}

export type FeedbackType = "feedback" | "feature_request";
export type FeedbackStatus =
  "new" | "acknowledged" | "todo" | "done" | "archived";
export type AuthorKind = "user" | "mcp";

export interface FeedbackMessageResponse {
  id: string;
  type: FeedbackType;
  title: string;
  body: string;
  author_email: string;
  author_display_name: string;
  author_kind: AuthorKind;
  status: FeedbackStatus;
  vote_count: number;
  created_at: string;
  updated_at: string;
}

export interface FeatureRequestRow {
  id: string;
  title: string;
  body: string;
  author_email: string;
  author_display_name: string;
  author_kind: AuthorKind;
  status: FeedbackStatus;
  vote_count: number;
  has_voted: boolean;
  created_at: string;
}

export interface FeatureRequestListResponse {
  items: FeatureRequestRow[];
}

export interface AdminFeedbackRow {
  id: string;
  type: FeedbackType;
  title: string;
  body: string;
  author_email: string;
  author_display_name: string;
  author_kind: AuthorKind;
  status: FeedbackStatus;
  vote_count: number;
  created_at: string;
  updated_at: string;
}

export interface AdminFeedbackListResponse {
  items: AdminFeedbackRow[];
  total: number;
}

export interface AdminFeedbackFilters {
  status?: string;
  type?: FeedbackType | string;
  author_kind?: AuthorKind | string;
  q?: string;
}

export type Visibility = "private" | "unlisted" | "shared";

export interface ArtifactPublicView {
  slug: string;
  title: string;
  description: string;
  visibility: Visibility;
  owner_email: string;
  url: string;
  created_at: string;
  updated_at: string;
  expires_at: string;
  size_bytes: number;
  view_count: number;
  last_viewed_at: string | null;
  permanent: boolean;
  // Editing-context Markdown. Populated only when the caller can edit
  // (owner or editor); null in every other view.
  instructions: string | null;
  tags: string[];
  /**
   * Optimistic-concurrency token — bumped by every editor-visible mutation.
   * Send it back as ``if_version`` on writes (mandatory once the artifact
   * has editors); a stale value is a 409.
   */
  edit_version: number;
  /** The caller's relationship to this artifact. */
  access: "owner" | "editor" | "viewer";
  /** How many people the artifact is directly shared with. No raw emails here. */
  shared_with_count: number;
}

/** Response of the sharing management endpoints (GET owner-or-editor, PUT owner-only). */
export interface ArtifactSharingView {
  slug: string;
  owner_email: string;
  shared_with: string[];
  /** Subset of ``shared_with`` allowed to edit content. Never contains the owner. */
  editors: string[];
}

/** Filters accepted by ``GET /api/artifacts``. */
export interface ArtifactFilters {
  /**
   * ``mine`` lists the caller's artifacts; ``all`` adds every org-shared
   * artifact plus direct shares; ``shared_with_me`` lists only artifacts
   * explicitly shared with the caller.
   */
  scope: "mine" | "all" | "shared_with_me";
  /** Substring (case-insensitive) on title + description + slug. */
  q?: string;
  /** Narrow to one visibility level. */
  visibility?: Visibility;
  /** ``active`` (default) | ``expiring`` (<7d) | ``permanent`` | ``deleted`` (admin). */
  status?: "active" | "expiring" | "permanent" | "deleted";
  /** Owner email filter — silently ignored when ``scope=mine``. */
  owner?: string;
  /** Sort key. */
  sort?: "updated" | "created" | "views" | "title";
  limit?: number;
  offset?: number;
  tags?: string[];
}

export type AdminOverviewBucket = {
  ts: string;
  value: number;
};

export type AdminTopArtifact = {
  slug: string;
  title: string;
  owner_email: string;
  view_count: number;
};

export type AdminTopAuthor = {
  email: string;
  artifact_count: number;
};

export type AdminOverviewRange = "24h" | "7d" | "30d";

export type AdminOverview = {
  range: AdminOverviewRange;
  bucket: "hour" | "day";
  // Adoption tiles
  total_artifacts: number;
  artifacts_in_range: number;
  active_authors_in_range: number;
  total_users: number;
  // Adoption charts
  artifacts_per_bucket: AdminOverviewBucket[];
  active_authors_per_bucket: AdminOverviewBucket[];
  // Adoption tables
  top_artifacts_by_views: AdminTopArtifact[];
  top_authors_by_creates: AdminTopAuthor[];
  // Cost tiles
  anthropic_requests: number;
  anthropic_tokens: number;
  bigquery_queries: number;
  bigquery_bytes_scanned: number;
  bigquery_est_cost_usd: number;
  // Ops tiles
  anthropic_errors: number;
  bigquery_denials: number;
  rate_limited: number;
};

export type AdminBigQuerySettings = {
  enabled: boolean;
  max_rows_per_query: number;
  max_bytes_per_query: number;
  per_user_queries_per_hour: number;
  per_user_bytes_per_hour: number;
  default_cache_ttl_seconds_cap: number;
  default_project: string | null;
  default_location: string;
  allowlist_learning_mode_enabled: boolean;
  allowlist_learning_mode_expires_at: string | null;
};

export type AdminBigQueryAllowlistEntry = {
  id: string;
  project: string;
  dataset: string;
  table_pattern: string;
  added_by: string;
  added_at: string;
  note: string | null;
  auto_added: boolean;
  source: string | null;
  triggering_sql: string | null;
  triggering_sql_hash: string | null;
  reviewed: boolean;
  reviewed_by: string | null;
  reviewed_at: string | null;
};

export type AdminBigQueryMetrics = {
  range: "24h" | "7d" | "30d";
  queries: number;
  bytes_scanned: number;
  est_cost_usd: number;
  denied_count: number;
  cache_hit_rate: number;
  top_actors: Array<{ email: string; count: number }>;
  top_datasets: Array<{ name: string; count: number }>;
};

export type AdminBigQueryLogEntry = {
  id: string;
  actor_email: string;
  slug: string | null;
  sql_hash: string;
  sql_truncated: string;
  referenced_tables: string[] | null;
  bytes_scanned: number;
  rows_returned: number;
  duration_ms: number;
  cached: boolean;
  outcome:
    | "success"
    | "disabled"
    | "rate_limited"
    | "write_attempted"
    | "allowlist_denied"
    | "cap_exceeded"
    | "upstream_error";
  error_detail: string | null;
  created_at: string;
};

/** Jira proxy settings as returned by GET/PUT /api/admin/jira/settings. */
export type AdminJiraSettings = {
  enabled: boolean;
  allowed_projects: string[];
  max_issues_per_query: number;
  per_user_queries_per_hour: number;
  default_cache_ttl_seconds_cap: number;
  updated_at: string | null;
  updated_by: string;
};

/** Partial PUT body; the backend rejects unknown keys and an empty patch. */
export type AdminJiraSettingsPatch = Partial<
  Pick<
    AdminJiraSettings,
    | "enabled"
    | "allowed_projects"
    | "max_issues_per_query"
    | "per_user_queries_per_hour"
    | "default_cache_ttl_seconds_cap"
  >
>;

/** Every outcome the Jira proxy returns (mirrors jira_proxy/router.py STATUS_FOR). */
export const JIRA_OUTCOMES = [
  "success",
  "disabled",
  "unavailable",
  "rate_limited",
  "invalid_request",
  "allowlist_denied",
  "jql_rejected",
  "invalid_jql",
  "cap_exceeded",
  "upstream_rate_limited",
  "upstream_auth_failed",
  "upstream_error",
] as const;

/** One Jira proxy outcome code. */
export type AdminJiraOutcome = (typeof JIRA_OUTCOMES)[number];

/** One row of GET /api/admin/jira/queries. */
export type AdminJiraLogEntry = {
  id: string;
  actor_email: string;
  source: "browser" | "mcp";
  slug: string | null;
  kind: "search" | "count";
  jql_hash: string;
  jql_truncated: string;
  fields: string[];
  issues_returned: number;
  pages: number;
  duration_ms: number;
  cached: boolean;
  truncated: boolean;
  outcome: AdminJiraOutcome;
  error_detail: string | null;
  created_at: string;
};

/** Result of GET /api/admin/jira/connection (door-1 check). */
export type AdminJiraConnection = {
  configured: boolean;
  ok: boolean;
  account?: {
    account_id: string | null;
    display_name: string | null;
    account_type: string | null;
  };
  visible_projects?: string[];
  /** False when the account sees more projects than the check can list. */
  complete?: boolean;
  visible_not_allowlisted?: string[];
  allowlisted_not_visible?: string[];
  /** Allowlisted keys that still resolve under a newer key: update the entry; not drift. */
  allowlisted_renamed?: Record<string, string>;
  error?: string;
  detail?: string;
};

/** Server-side filters for the recent-queries list. */
export type AdminJiraQueryFilters = {
  outcome?: string;
  actor?: string;
  slug?: string;
  range?: "24h" | "7d" | "30d";
};

export interface ArtifactVersionFile {
  path: string;
  size_bytes: number;
  content_type: string;
}

export interface ArtifactVersion {
  version_id: string;
  slug: string;
  created_at: string;
  actor_email: string;
  actor_kind: string;
  revision_note: string | null;
  title: string;
  description: string;
  visibility: Visibility;
  size_bytes: number;
  files: ArtifactVersionFile[];
  restored_from_version_id: string | null;
  instructions: string;
}

export interface GoogleSheetsIntegrationStatus {
  available: boolean;
  connected: boolean;
  scope_mode: "none" | "read_only" | "read_write";
}

/**
 * Error thrown for every non-OK response, carrying the HTTP status so callers
 * can branch on it (e.g. 409/422 write conflicts) instead of parsing the
 * message. ``message`` keeps the historical ``"<status> <statusText>: <body>"``
 * shape that {@link apiErrorDetail} unpacks.
 */
export class ApiError extends Error {
  /** HTTP status code of the failed response. */
  readonly status: number;
  /** Raw response body, exactly as returned by the server. */
  readonly body: string;

  constructor(status: number, statusText: string, body: string) {
    super(`${status} ${statusText}: ${body}`);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const r = await fetch(path, {
    credentials: "include",
    headers: { "Content-Type": "application/json", ...(init.headers || {}) },
    ...init,
  });
  if (r.status === 401) {
    // Session expired — bounce through SSO with the current location preserved.
    // Backend reads ``?next=`` (Query alias on next_path); ``?next_path=`` is ignored.
    const next = encodeURIComponent(
      window.location.pathname + window.location.search,
    );
    window.location.href = `/api/auth/google/login?next=${next}`;
    throw new Error("redirecting-to-login");
  }
  if (!r.ok) {
    throw new ApiError(r.status, r.statusText, await r.text());
  }
  if (r.status === 204) return undefined as T;
  return (await r.json()) as T;
}

/** Pull the backend's `detail` out of api.ts's "STATUS TEXT: {json}" error shape. */
export function apiErrorDetail(e: unknown): string {
  const msg = e instanceof Error ? e.message : String(e);
  const jsonStart = msg.indexOf("{");
  if (jsonStart !== -1) {
    try {
      const detail: unknown = (JSON.parse(msg.slice(jsonStart)) as { detail?: unknown }).detail;
      // FastAPI request-validation 422s put an ARRAY of objects in `detail`;
      // only a string is renderable — anything else falls back to the raw message.
      if (typeof detail === "string" && detail) return detail;
    } catch {
      /* fall through to the raw message */
    }
  }
  return msg;
}

export const api = {
  me(): Promise<UserAuth> {
    return call<UserAuth>("/api/auth/me");
  },
  logout(): Promise<{ ok: true }> {
    return call<{ ok: true }>("/api/auth/logout", { method: "POST" });
  },
  googleSheets: {
    integration: {
      /** Return the active viewer's non-secret Sheets connection metadata. */
      get(): Promise<GoogleSheetsIntegrationStatus> {
        return call<GoogleSheetsIntegrationStatus>(
          "/api/google-sheets/integration",
        );
      },
    },
  },
  /**
   * Unified artifact listing — replaces myArtifacts() + sharedArtifacts().
   *
   * Calls ``GET /api/artifacts`` with the supplied filters serialised into
   * query params. Empty / undefined values are dropped so the URL stays clean.
   */
  listArtifacts(filters: ArtifactFilters): Promise<ArtifactPublicView[]> {
    const params = new URLSearchParams();
    params.set("scope", filters.scope);
    if (filters.q) params.set("q", filters.q);
    if (filters.visibility) params.set("visibility", filters.visibility);
    if (filters.status) params.set("status", filters.status);
    if (filters.owner) params.set("owner", filters.owner);
    if (filters.sort) params.set("sort", filters.sort);
    if (filters.limit !== undefined) params.set("limit", String(filters.limit));
    if (filters.offset !== undefined)
      params.set("offset", String(filters.offset));
    filters.tags?.forEach((t) => params.append("tags", t));
    return call<ArtifactPublicView[]>(`/api/artifacts?${params.toString()}`);
  },
  getOne(slug: string): Promise<ArtifactPublicView> {
    return call<ArtifactPublicView>(`/api/artifacts/${slug}`);
  },
  listVersions(slug: string): Promise<ArtifactVersion[]> {
    return call<ArtifactVersion[]>(`/api/artifacts/${slug}/versions`);
  },
  /**
   * Restore a prior version as the new live state.
   *
   * Both fields are optional over the wire and only the supplied ones are
   * sent — with neither, the POST stays bodyless (solo-owner artifacts need
   * no token and no note). On a multi-editor artifact the server 422s a
   * missing/blank ``revisionNote`` and 409s a stale ``ifVersion``.
   */
  restoreVersion(
    slug: string,
    versionId: string,
    opts: { ifVersion?: number; revisionNote?: string } = {},
  ): Promise<ArtifactPublicView> {
    const body: Record<string, unknown> = {};
    if (opts.ifVersion !== undefined) body.if_version = opts.ifVersion;
    if (opts.revisionNote) body.revision_note = opts.revisionNote;
    return call<ArtifactPublicView>(
      `/api/artifacts/${slug}/restore/${versionId}`,
      {
        method: "POST",
        ...(Object.keys(body).length ? { body: JSON.stringify(body) } : {}),
      },
    );
  },
  deleteArtifact(slug: string): Promise<ArtifactPublicView> {
    return call<ArtifactPublicView>(`/api/artifacts/${slug}`, {
      method: "DELETE",
    });
  },
  renewArtifact(slug: string): Promise<ArtifactPublicView> {
    return call<ArtifactPublicView>(`/api/artifacts/${slug}/renew`, {
      method: "POST",
    });
  },
  setVisibility(
    slug: string,
    visibility: Visibility,
  ): Promise<ArtifactPublicView> {
    return call<ArtifactPublicView>(`/api/artifacts/${slug}`, {
      method: "PATCH",
      body: JSON.stringify({ visibility }),
    });
  },
  setPermanent(slug: string, permanent: boolean): Promise<ArtifactPublicView> {
    return call<ArtifactPublicView>(`/api/artifacts/${slug}`, {
      method: "PATCH",
      body: JSON.stringify({ permanent }),
    });
  },
  /**
   * Replace the tag list. ``ifVersion`` is the artifact's current
   * ``edit_version``: the server mandates it (422) once the artifact has
   * editors and 409s a stale value, so the caller must hold a fresh one.
   */
  setTags(
    slug: string,
    tags: string[],
    ifVersion: number,
  ): Promise<ArtifactPublicView> {
    return call<ArtifactPublicView>(`/api/artifacts/${slug}`, {
      method: "PATCH",
      body: JSON.stringify({ tags, if_version: ifVersion }),
    });
  },
  getSharing(slug: string): Promise<ArtifactSharingView> {
    return call<ArtifactSharingView>(`/api/artifacts/${slug}/sharing`);
  },
  /**
   * Replace the sharing lists. Owner-only (403 otherwise), per-list replace:
   * omitting ``editors`` preserves the stored editor list, sending it (even
   * ``[]``) replaces it whole. Editors must also be in ``shared_with`` — the
   * server 422s a stranded editor.
   */
  setSharing(
    slug: string,
    shared_with: string[],
    editors?: string[],
  ): Promise<ArtifactSharingView> {
    return call<ArtifactSharingView>(`/api/artifacts/${slug}/sharing`, {
      method: "PUT",
      body: JSON.stringify(
        editors === undefined ? { shared_with } : { shared_with, editors },
      ),
    });
  },
  admin: {
    listUsers(): Promise<AdminUserView[]> {
      return call<AdminUserView[]>("/api/admin/users");
    },
    inviteUser(email: string): Promise<AdminUserView> {
      return call<AdminUserView>("/api/admin/users", {
        method: "POST",
        body: JSON.stringify({ email }),
      });
    },
    patchUser(
      id: string,
      patch: { role?: "user" | "admin"; is_active?: boolean },
    ): Promise<AdminUserView> {
      return call<AdminUserView>(`/api/admin/users/${id}`, {
        method: "PATCH",
        body: JSON.stringify(patch),
      });
    },
    usage(): Promise<AdminUsageRow[]> {
      return call<AdminUsageRow[]>("/api/admin/usage");
    },
    overview(range: AdminOverviewRange): Promise<AdminOverview> {
      return call<AdminOverview>(`/api/admin/overview?range=${range}`);
    },
    artifacts: {
      list(filters: AdminArtifactFilters = {}): Promise<AdminArtifactView[]> {
        const params = new URLSearchParams();
        if (filters.visibility) params.set("visibility", filters.visibility);
        if (filters.owner) params.set("owner", filters.owner);
        if (filters.status) params.set("status", filters.status);
        if (filters.q) params.set("q", filters.q);
        if (filters.stale_days !== undefined) {
          params.set("stale_days", String(filters.stale_days));
        }
        if (filters.sort) params.set("sort", filters.sort);
        if (filters.direction) params.set("direction", filters.direction);
        if (filters.limit !== undefined)
          params.set("limit", String(filters.limit));
        if (filters.offset !== undefined)
          params.set("offset", String(filters.offset));
        const qs = params.toString();
        return call<AdminArtifactView[]>(
          `/api/admin/artifacts${qs ? "?" + qs : ""}`,
        );
      },
      setVisibility(
        slug: string,
        visibility: Visibility,
      ): Promise<AdminArtifactView> {
        return call<AdminArtifactView>(
          `/api/admin/artifacts/${slug}/visibility`,
          { method: "PATCH", body: JSON.stringify({ visibility }) },
        );
      },
      setPermanent(
        slug: string,
        permanent: boolean,
      ): Promise<AdminArtifactView> {
        return call<AdminArtifactView>(
          `/api/admin/artifacts/${slug}/permanent`,
          { method: "PATCH", body: JSON.stringify({ permanent }) },
        );
      },
      restore(slug: string): Promise<AdminArtifactView> {
        return call<AdminArtifactView>(`/api/admin/artifacts/${slug}/restore`, {
          method: "POST",
        });
      },
      forceDelete(
        slug: string,
      ): Promise<{ slug: string; force_deleted: true }> {
        return call<{ slug: string; force_deleted: true }>(
          `/api/admin/artifacts/${slug}?force=true`,
          { method: "DELETE" },
        );
      },
    },
    quotas: {
      get(email: string): Promise<AdminQuotasView> {
        return call<AdminQuotasView>(
          `/api/admin/quotas/${encodeURIComponent(email)}`,
        );
      },
      patch(email: string, patch: AdminQuotasPatch): Promise<AdminQuotasView> {
        return call<AdminQuotasView>(
          `/api/admin/quotas/${encodeURIComponent(email)}`,
          { method: "PATCH", body: JSON.stringify(patch) },
        );
      },
    },
    auditLog(filters: AuditLogFilters = {}): Promise<AuditLogEntry[]> {
      const params = new URLSearchParams();
      if (filters.actor) params.set("actor", filters.actor);
      if (filters.event_type) params.set("event_type", filters.event_type);
      if (filters.outcome) params.set("outcome", filters.outcome);
      if (filters.since) params.set("since", filters.since);
      if (filters.until) params.set("until", filters.until);
      if (filters.limit !== undefined)
        params.set("limit", String(filters.limit));
      if (filters.offset !== undefined)
        params.set("offset", String(filters.offset));
      const qs = params.toString();
      return call<AuditLogEntry[]>(`/api/admin/audit-log${qs ? "?" + qs : ""}`);
    },
    tokens: {
      list(): Promise<AdminTokenView[]> {
        return call<AdminTokenView[]>("/api/admin/tokens");
      },
      mintOnBehalfOf(email: string, label: string): Promise<AdminTokenCreated> {
        return call<AdminTokenCreated>(
          `/api/admin/tokens/on-behalf-of/${encodeURIComponent(email)}`,
          { method: "POST", body: JSON.stringify({ label }) },
        );
      },
      revoke(id: string): Promise<{ ok: true }> {
        return call<{ ok: true }>(`/api/admin/tokens/${id}`, {
          method: "DELETE",
        });
      },
    },
    cdnAllowlist: {
      get(): Promise<CdnAllowlistDoc> {
        return call<CdnAllowlistDoc>("/api/admin/cdn-allowlist");
      },
      put(input: CdnAllowlistInput): Promise<CdnAllowlistDoc> {
        return call<CdnAllowlistDoc>("/api/admin/cdn-allowlist", {
          method: "PUT",
          body: JSON.stringify(input),
        });
      },
    },
    sharingSettings: {
      get(): Promise<SharingSettingsDoc> {
        return call<SharingSettingsDoc>("/api/admin/sharing-settings");
      },
      put(domains: string[]): Promise<SharingSettingsDoc> {
        return call<SharingSettingsDoc>("/api/admin/sharing-settings", {
          method: "PUT",
          body: JSON.stringify({ allowed_share_domains: domains }),
        });
      },
    },
    lifecycle: {
      get(): Promise<LifecycleConfigDoc> {
        return call<LifecycleConfigDoc>("/api/admin/lifecycle");
      },
      put(input: LifecycleConfigInput): Promise<LifecyclePutResponse> {
        return call<LifecyclePutResponse>("/api/admin/lifecycle", {
          method: "PUT",
          body: JSON.stringify(input),
        });
      },
    },
    feedback: {
      list(
        filters: AdminFeedbackFilters = {},
      ): Promise<AdminFeedbackListResponse> {
        const params = new URLSearchParams();
        if (filters.status) params.set("status", filters.status);
        if (filters.type) params.set("type", filters.type);
        if (filters.author_kind) params.set("author_kind", filters.author_kind);
        if (filters.q) params.set("q", filters.q);
        const qs = params.toString();
        return call<AdminFeedbackListResponse>(
          `/api/admin/feedback${qs ? "?" + qs : ""}`,
        );
      },
      patchStatus(
        id: string,
        newStatus: FeedbackStatus,
      ): Promise<AdminFeedbackRow> {
        return call<AdminFeedbackRow>(`/api/admin/feedback/${id}`, {
          method: "PATCH",
          body: JSON.stringify({ status: newStatus }),
        });
      },
      remove(id: string): Promise<void> {
        return call<void>(`/api/admin/feedback/${id}`, { method: "DELETE" });
      },
    },
    bigquery: {
      settings: {
        get(): Promise<AdminBigQuerySettings> {
          return call<AdminBigQuerySettings>("/api/admin/bigquery/settings");
        },
        put(
          patch: Partial<AdminBigQuerySettings>,
        ): Promise<AdminBigQuerySettings> {
          return call<AdminBigQuerySettings>("/api/admin/bigquery/settings", {
            method: "PUT",
            body: JSON.stringify(patch),
          });
        },
      },
      allowlist: {
        listAll(): Promise<{ items: AdminBigQueryAllowlistEntry[] }> {
          return call<{ items: AdminBigQueryAllowlistEntry[] }>(
            "/api/admin/bigquery/allowlist/all",
          );
        },
        reviewPending(): Promise<{ items: AdminBigQueryAllowlistEntry[] }> {
          return call<{ items: AdminBigQueryAllowlistEntry[] }>(
            "/api/admin/bigquery/allowlist/review-pending",
          );
        },
        list(
          limit = 100,
          offset = 0,
        ): Promise<{ items: AdminBigQueryAllowlistEntry[] }> {
          return call<{ items: AdminBigQueryAllowlistEntry[] }>(
            `/api/admin/bigquery/allowlist?limit=${limit}&offset=${offset}`,
          );
        },
        add(body: {
          project: string;
          dataset: string;
          table_pattern?: string;
          note?: string;
        }): Promise<AdminBigQueryAllowlistEntry> {
          return call<AdminBigQueryAllowlistEntry>(
            "/api/admin/bigquery/allowlist",
            {
              method: "POST",
              body: JSON.stringify(body),
            },
          );
        },
        remove(id: string): Promise<void> {
          return call<void>(`/api/admin/bigquery/allowlist/${id}`, {
            method: "DELETE",
          });
        },
        review(id: string): Promise<AdminBigQueryAllowlistEntry> {
          return call<AdminBigQueryAllowlistEntry>(
            `/api/admin/bigquery/allowlist/${id}/review`,
            { method: "POST" },
          );
        },
      },
      metrics(range: "24h" | "7d" | "30d"): Promise<AdminBigQueryMetrics> {
        return call<AdminBigQueryMetrics>(
          `/api/admin/bigquery/metrics?range=${range}`,
        );
      },
      queries(
        limit = 50,
        offset = 0,
        filters: {
          outcome?: string;
          actor?: string;
          slug?: string;
          range?: "24h" | "7d" | "30d";
        } = {},
      ): Promise<{ items: AdminBigQueryLogEntry[] }> {
        const p = new URLSearchParams({
          limit: String(limit),
          offset: String(offset),
        });
        if (filters.outcome) p.set("outcome", filters.outcome);
        if (filters.actor) p.set("actor", filters.actor);
        if (filters.slug) p.set("slug", filters.slug);
        if (filters.range) p.set("range", filters.range);
        return call<{ items: AdminBigQueryLogEntry[] }>(
          `/api/admin/bigquery/queries?${p.toString()}`,
        );
      },
    },
    jira: {
      settings: {
        get(): Promise<AdminJiraSettings> {
          return call<AdminJiraSettings>("/api/admin/jira/settings");
        },
        put(patch: AdminJiraSettingsPatch): Promise<AdminJiraSettings> {
          return call<AdminJiraSettings>("/api/admin/jira/settings", {
            method: "PUT",
            body: JSON.stringify(patch),
          });
        },
      },
      queries(
        limit = 50,
        offset = 0,
        filters: AdminJiraQueryFilters = {},
      ): Promise<{ items: AdminJiraLogEntry[] }> {
        const p = new URLSearchParams({
          limit: String(limit),
          offset: String(offset),
        });
        if (filters.outcome) p.set("outcome", filters.outcome);
        if (filters.actor) p.set("actor", filters.actor);
        if (filters.slug) p.set("slug", filters.slug);
        if (filters.range) p.set("range", filters.range);
        return call<{ items: AdminJiraLogEntry[] }>(
          `/api/admin/jira/queries?${p.toString()}`,
        );
      },
      connection(): Promise<AdminJiraConnection> {
        return call<AdminJiraConnection>("/api/admin/jira/connection");
      },
    },
    alerts: {
      get(): Promise<AdminAlertSettings> {
        return call<AdminAlertSettings>("/api/admin/alerts/settings");
      },
      put(patch: AdminAlertSettingsPatch): Promise<AdminAlertSettings> {
        return call<AdminAlertSettings>("/api/admin/alerts/settings", {
          method: "PUT",
          body: JSON.stringify(patch),
        });
      },
      test(): Promise<AlertTestResult> {
        return call<AlertTestResult>("/api/admin/alerts/test", {
          method: "POST",
        });
      },
    },
  },
  feedback: {
    submit(input: {
      type: FeedbackType;
      title: string;
      body: string;
    }): Promise<FeedbackMessageResponse> {
      return call<FeedbackMessageResponse>("/api/feedback", {
        method: "POST",
        body: JSON.stringify(input),
      });
    },
    listFeatureRequests(): Promise<FeatureRequestListResponse> {
      return call<FeatureRequestListResponse>("/api/feedback/feature-requests");
    },
    toggleVote(
      id: string,
    ): Promise<{ vote_count: number; has_voted: boolean }> {
      return call<{ vote_count: number; has_voted: boolean }>(
        `/api/feedback/${id}/vote`,
        { method: "POST" },
      );
    },
  },
};
